/**
 * Jain Gaurav Yatra — Breakfast Options CRUD API.
 * Backend: /jain_gaurav_yatra/breakfast-options/  (cookie-session auth + X-Tenant-ID).
 * Create/update use multipart/form-data because `image` is an uploaded file (served back from
 * /media/jgy/breakfast/…). Paginated list response.
 *
 * `kind_display` is the server's human label for the `kind` slug (read-only).
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

/** Tenant + optional Bearer. Never sets Content-Type so the browser can add the multipart boundary. */
function authHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d?.message) ??
    pick(d?.detail) ??
    (Array.isArray(d?.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const BREAKFAST_PATH = "/jain_gaurav_yatra/breakfast-options/";

/** The one kind we know the API accepts. Others are merged in from existing rows at render time. */
export const KNOWN_KINDS: { value: string; label: string }[] = [
  { value: "street_food", label: "Street Food" },
];

/** A single dish inside a breakfast group. */
export interface BreakfastItem {
  id: string;
  name: string;
  /** Image URL served by the backend (may be null until processed). */
  image: string;
  display_number: number;
}

/**
 * A breakfast group: shared serving details plus the dishes served together.
 * The backend keys these by `kind` (one group per kind) and nests the dishes under `items`.
 */
export interface BreakfastOption {
  id: string;
  /** Legacy top-level name the API still echoes (usually the first dish); prefer `items`. */
  name: string;
  kind: string;
  /** Server's human label for `kind` (read-only). */
  kind_display: string;
  /** Group-level image, if the API ever sets one; dishes carry their own images. */
  image: string;
  display_number: number;
  serving_time: string;
  serving_location: string;
  is_active: boolean;
  /** The dishes in this group. */
  items: BreakfastItem[];
  created_at: string;
}

/** Fields accepted when creating/updating. `kind_display` is read-only server-side. */
export interface BreakfastWrite {
  name: string;
  kind: string;
  /** New image chosen in the editor. Omitted on the wire when null so the server keeps the existing one. */
  imageFile?: File | null;
  display_number: number;
  serving_time: string;
  serving_location: string;
  is_active: boolean;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function num(v: unknown): number {
  if (v === null || v === undefined || v === "") {return 0;}
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toItem(row: Record<string, unknown>): BreakfastItem {
  return {
    id: str(row.id),
    name: str(row.name),
    image: str(row.image),
    display_number: num(row.display_number),
  };
}

function toBreakfast(row: Record<string, unknown>): BreakfastOption {
  const rawItems = Array.isArray(row.items) ? (row.items as Record<string, unknown>[]) : [];
  const items = rawItems.map(toItem).sort((a, b) => a.display_number - b.display_number);
  return {
    id: str(row.id),
    name: str(row.name),
    kind: str(row.kind),
    kind_display: str(row.kind_display) || str(row.kind),
    image: str(row.image),
    display_number: num(row.display_number),
    serving_time: str(row.serving_time),
    serving_location: str(row.serving_location),
    is_active: row.is_active !== false,
    items,
    created_at: str(row.created_at),
  };
}

function toFormData(p: BreakfastWrite): FormData {
  const fd = new FormData();
  fd.append("name", p.name);
  fd.append("kind", p.kind);
  fd.append("display_number", String(p.display_number));
  fd.append("serving_time", p.serving_time);
  fd.append("serving_location", p.serving_location);
  // DRF BooleanField parses "true"/"false" under multipart.
  fd.append("is_active", p.is_active ? "true" : "false");
  // Only send the file when a new one was chosen; otherwise the server keeps the existing image.
  if (p.imageFile) {
    fd.append("image", p.imageFile);
  }
  return fd;
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

function rowsOf(data: unknown): Record<string, unknown>[] {
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return rows as Record<string, unknown>[];
}

export async function listBreakfast(): Promise<BreakfastOption[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${BREAKFAST_PATH}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return rowsOf(data)
    .map(toBreakfast)
    .sort((a, b) => a.display_number - b.display_number);
}

export async function createBreakfast(payload: BreakfastWrite): Promise<BreakfastOption> {
  const base = apiBase();
  const res = await fetch(`${base}${BREAKFAST_PATH}`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: toFormData(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toBreakfast(data as Record<string, unknown>);
}

/** One dish inside a bulk create — its own name/photo, sharing the group's serving fields. */
export interface BreakfastItemInput {
  name: string;
  imageFile?: File | null;
  display_number?: number;
}

/** Create several breakfast items in one request; they share kind + serving details. */
export interface BreakfastBulkWrite {
  kind: string;
  serving_time: string;
  serving_location: string;
  is_active: boolean;
  items: BreakfastItemInput[];
}

/**
 * Bulk create via POST /breakfast-options/bulk/ (multipart). Serialises items as
 * `items[i][name]` / `items[i][image]`, matching the backend's form contract.
 * Returns the created group (with its nested `items`).
 */
export async function createBreakfastBulk(payload: BreakfastBulkWrite): Promise<BreakfastOption> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const fd = new FormData();
  fd.append("kind", payload.kind);
  fd.append("serving_time", payload.serving_time);
  fd.append("serving_location", payload.serving_location);
  fd.append("is_active", payload.is_active ? "true" : "false");
  payload.items.forEach((it, i) => {
    fd.append(`items[${i}][name]`, it.name);
    if (it.imageFile) {
      fd.append(`items[${i}][image]`, it.imageFile);
    }
    if (it.display_number !== undefined && it.display_number !== null) {
      fd.append(`items[${i}][display_number]`, String(it.display_number));
    }
  });

  const res = await fetch(`${base}${BREAKFAST_PATH}bulk/`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: fd,
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toBreakfast(data as Record<string, unknown>);
}

export async function updateBreakfast(
  id: string,
  payload: BreakfastWrite,
): Promise<BreakfastOption> {
  const base = apiBase();
  const res = await fetch(`${base}${BREAKFAST_PATH}${id}/`, {
    method: "PATCH",
    headers: authHeaders(),
    credentials: "include",
    body: toFormData(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toBreakfast(data as Record<string, unknown>);
}

/** Group-level fields the admin can edit after creation (the dishes are managed separately). */
export interface BreakfastGroupWrite {
  kind: string;
  serving_time: string;
  serving_location: string;
  is_active: boolean;
}

/**
 * Update a breakfast group's shared serving details via PATCH /breakfast-options/{id}/.
 * JSON body (no file), so nested dishes are left untouched.
 */
export async function updateBreakfastGroup(
  id: string,
  p: BreakfastGroupWrite,
): Promise<BreakfastOption> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${BREAKFAST_PATH}${id}/`, {
    method: "PATCH",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      kind: p.kind,
      serving_time: p.serving_time,
      serving_location: p.serving_location,
      is_active: p.is_active,
    }),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toBreakfast(data as Record<string, unknown>);
}

export async function deleteBreakfast(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${BREAKFAST_PATH}${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    throw new Error(errMessage(data, res.status));
  }
}
